import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { setupGitHubAppInstallation } from "../api/sourceControl";
import {
  type DashboardOrganizationSummary,
  type DashboardPendingInvite,
  acceptInviteAsUser,
  createOrganizationAsUser,
  createSourceControlProviderAsUser,
  listSourceControlProvidersAsUser,
  selectOrganizationAsUser,
  setUpOrganizationBillingAsUser,
} from "../dashboard-client";
import { useDashboardAuth } from "./useDashboardAuth";
import {
  loadOnboardingBootstrapState,
  resolveInitialStep,
} from "./useOnboardingBootstrap";

export type OnboardingStep =
  | "pending-invites"
  | "create-org"
  | "github"
  | "jira"
  | "payment"
  | "done";

const STEPS: OnboardingStep[] = [
  "pending-invites",
  "create-org",
  "github",
  "jira",
  "payment",
  "done",
];

interface OnboardingState {
  loading: boolean;
  currentStep: OnboardingStep;
  completedSteps: Set<OnboardingStep>;
  organizations: DashboardOrganizationSummary[];
  pendingInvites: DashboardPendingInvite[];
  selectedOrgId: string | null;
  githubProviderId: string | null;
  error: string | null;
  submitting: boolean;
}

export function useOnboarding() {
  const auth = useDashboardAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState<OnboardingState>({
    completedSteps: new Set(),
    currentStep: "create-org",
    error: null,
    githubProviderId: null,
    loading: true,
    organizations: [],
    pendingInvites: [],
    selectedOrgId: null,
    submitting: false,
  });

  const initializeState = useCallback(async () => {
    setState((prev) => ({ ...prev, error: null, loading: true }));
    try {
      const bootstrap = await loadOnboardingBootstrapState(auth.organizationId);
      const completed = new Set<OnboardingStep>(bootstrap.completed);
      const currentStep = resolveInitialStep(
        searchParams.get("step"),
        completed,
        STEPS,
      );

      setState({
        completedSteps: completed,
        currentStep,
        error: null,
        githubProviderId: bootstrap.githubProviderId,
        loading: false,
        organizations: bootstrap.organizations,
        pendingInvites: bootstrap.pendingInvites,
        selectedOrgId: bootstrap.selectedOrgId,
        submitting: false,
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load onboarding state.";
      setState((prev) => ({ ...prev, error: message, loading: false }));
    }
  }, [auth.organizationId, searchParams]);

  useEffect(() => {
    void initializeState();
  }, [initializeState]);

  const markComplete = useCallback((step: OnboardingStep) => {
    setState((prev) => {
      const completed = new Set(prev.completedSteps);
      completed.add(step);
      const nextStep =
        STEPS.find((s) => s !== "done" && !completed.has(s)) ?? "done";
      return { ...prev, completedSteps: completed, currentStep: nextStep };
    });
  }, []);

  const requireSelectedOrg = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: "create-org",
      error: "Select or create an organization to continue onboarding.",
      submitting: false,
    }));
  }, []);

  const createOrg = useCallback(
    async (name: string, slug: string) => {
      setState((prev) => ({ ...prev, error: null, submitting: true }));
      try {
        const createOrgResult = await createOrganizationAsUser({
          body: { name, slug },
        });
        const org = createOrgResult.data;
        await selectOrganizationAsUser({
          body: { organization_id: org.organization_id },
        });
        await auth.refreshSession();
        setState((prev) => {
          const completed = new Set(prev.completedSteps);
          completed.add("create-org");
          return {
            ...prev,
            completedSteps: completed,
            currentStep: "github",
            organizations: [...prev.organizations, org],
            selectedOrgId: org.organization_id,
            submitting: false,
          };
        });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to create organization.";
        setState((prev) => ({ ...prev, error: message, submitting: false }));
      }
    },
    [auth],
  );

  const selectExistingOrg = useCallback(
    async (orgId: string) => {
      setState((prev) => ({ ...prev, error: null, submitting: true }));
      try {
        await selectOrganizationAsUser({ body: { organization_id: orgId } });
        await auth.refreshSession();
        setState((prev) => {
          const completed = new Set(prev.completedSteps);
          completed.add("create-org");
          return {
            ...prev,
            completedSteps: completed,
            currentStep: "github",
            selectedOrgId: orgId,
            submitting: false,
          };
        });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to select organization.";
        setState((prev) => ({ ...prev, error: message, submitting: false }));
      }
    },
    [auth],
  );

  const acceptInviteAndProceed = useCallback(
    async (token: string) => {
      setState((prev) => ({ ...prev, error: null, submitting: true }));
      try {
        const acceptResult = await acceptInviteAsUser({ path: { token } });
        const result = acceptResult.data;
        await auth.refreshSession();
        const orgId = result.organization_id ?? state.selectedOrgId;
        if (!orgId) {
          throw new Error(
            "Invite accepted, but no organization is selected. Please select an organization to continue.",
          );
        }
        setState((prev) => {
          const completed = new Set(prev.completedSteps);
          completed.add("pending-invites");
          completed.add("create-org");
          return {
            ...prev,
            completedSteps: completed,
            currentStep: "github",
            pendingInvites: prev.pendingInvites.filter(
              (i) => i.token !== token,
            ),
            selectedOrgId: orgId,
            submitting: false,
          };
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to accept invite.";
        setState((prev) => ({ ...prev, error: message, submitting: false }));
      }
    },
    [auth, state.selectedOrgId],
  );

  const installGitHub = useCallback(async () => {
    if (!state.selectedOrgId) {
      requireSelectedOrg();
      return;
    }
    setState((prev) => ({ ...prev, error: null, submitting: true }));
    try {
      const listProvidersResult = await listSourceControlProvidersAsUser();
      const { providers } = listProvidersResult.data;
      const existing = providers.find((p) => p.type === "github");

      let githubProvider;
      if (existing) {
        githubProvider = existing;
      } else {
        const createProviderResult = await createSourceControlProviderAsUser({
          body: {
            display_name: "GitHub",
            provider_id: `github-${state.selectedOrgId}`,
            tenant_id: state.selectedOrgId,
            type: "github",
          },
        });
        githubProvider = createProviderResult.data.provider;
      }

      const { redirect_url } = await setupGitHubAppInstallation(
        githubProvider.provider_id,
        { redirect_url: `${window.location.origin}/onboarding?step=github` },
      );
      window.location.assign(redirect_url);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to start GitHub install.";
      setState((prev) => ({ ...prev, error: message, submitting: false }));
    }
  }, [requireSelectedOrg, state.selectedOrgId]);

  const setupPayment = useCallback(async () => {
    if (!state.selectedOrgId) {
      requireSelectedOrg();
      return;
    }
    setState((prev) => ({ ...prev, error: null, submitting: true }));
    try {
      await setUpOrganizationBillingAsUser({
        path: { organizationId: state.selectedOrgId },
      });
      setState((prev) => {
        const completed = new Set(prev.completedSteps);
        completed.add("payment");
        const nextStep =
          STEPS.find((s) => s !== "done" && !completed.has(s)) ?? "done";
        return {
          ...prev,
          completedSteps: completed,
          currentStep: nextStep,
          submitting: false,
        };
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to set up billing.";
      setState((prev) => ({ ...prev, error: message, submitting: false }));
    }
  }, [requireSelectedOrg, state.selectedOrgId]);

  const skipStep = useCallback(() => {
    setState((prev) => {
      const currentIdx = STEPS.indexOf(prev.currentStep);
      const nextStep =
        currentIdx < STEPS.length - 1 ? STEPS[currentIdx + 1] : "done";
      return { ...prev, currentStep: nextStep };
    });
  }, []);

  const goToStep = useCallback((step: OnboardingStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const finishOnboarding = useCallback(() => {
    void navigate("/overview");
  }, [navigate]);

  return useMemo(
    () => ({
      ...state,
      acceptInviteAndProceed,
      createOrg,
      finishOnboarding,
      goToStep,
      installGitHub,
      markComplete,
      selectExistingOrg,
      setupPayment,
      skipStep,
      steps: STEPS,
    }),
    [
      state,
      createOrg,
      selectExistingOrg,
      acceptInviteAndProceed,
      installGitHub,
      setupPayment,
      skipStep,
      goToStep,
      markComplete,
      finishOnboarding,
    ],
  );
}
